import axios from 'axios';

// Same-origin in the unified app; cookies carry the session.
const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // 401 → bounce to Microsoft sign-in so the user lands back signed in.
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const onLogin = window.location.pathname.startsWith('/login');
      if (!onLogin) window.location.href = '/auth/microsoft';
    }
    const message = error.response?.data?.message || error.message || 'An error occurred';
    return Promise.reject(new Error(message));
  }
);

export const trainingApi = {
  // Dashboard & Statistics
  getDashboard: () => api.get('/training/dashboard'),
  getPMDashboard: (pmId) => api.get(`/training/dashboard/${pmId}`),
  getAdminDashboard: () => api.get('/training/admin-dashboard'),
  
  // Training Modules
  getModules: (certificationTrack) => api.get('/training/modules', { params: { certificationTrack } }),
  getModule: (id) => api.get(`/training/modules/${id}`),
  createModule: (moduleData) => api.post('/training/modules', moduleData),
  updateModule: (id, moduleData) => api.put(`/training/modules/${id}`, moduleData),
  deleteModule: (id) => api.delete(`/training/modules/${id}`),
  toggleModuleStatus: (id, active) => api.patch(`/training/modules/${id}/status`, { active }),
  
  // Project Managers
  getPMs: () => api.get('/training/pms'),
  getPM: (id) => api.get(`/training/pms/${id}`),
  createPM: (pmData) => api.post('/training/pms', pmData),
  updatePM: (id, pmData) => api.put(`/training/pms/${id}`, pmData),
  deletePM: (id) => api.delete(`/training/pms/${id}`),
  togglePMStatus: (id, active) => api.patch(`/training/pms/${id}/status`, { active }),
  
  // Progress & Assignments
  getProgress: (pmId, status, dueSoon) => api.get('/training/progress', { params: { pmId, status, dueSoon } }),
  assignModule: (pmId, moduleId, dueDate, totalSteps) => 
    api.post('/training/progress', { pmId, moduleId, dueDate, totalSteps }),
  updateProgress: (id, updates) => api.patch(`/training/progress/${id}`, updates),
  
  // Quizzes
  getQuizzes: (moduleId) => api.get(`/training/quizzes/${moduleId}`),
  createQuiz: (quizData) => api.post('/training/quizzes', quizData),
  updateQuiz: (id, quizData) => api.put(`/training/quizzes/${id}`, quizData),
  deleteQuiz: (id) => api.delete(`/training/quizzes/${id}`),
  toggleQuizStatus: (id, active) => api.patch(`/training/quizzes/${id}/status`, { active }),
  submitQuiz: (progressRecordId, quizId, pmAnswer, score, passed) =>
    api.post('/training/quizzes/submit', { progressRecordId, quizId, pmAnswer, score, passed }),
  getQuizPerformance: (pmId) => api.get(`/training/quizzes/performance/${pmId}`),
  
  // SOP Upload
  uploadSOP: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/training/sops/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  
  // Workload (from Quickbase)
  getWorkload: () => api.get('/training/workload'),
  getPMWorkload: (email) => api.get(`/training/workload/${email}`),
  getAvailableOrders: () => api.get('/training/available-orders'),

  // Auto-Assignment
  getOrdersForAutoAssignment: () => api.get('/training/orders-for-auto-assignment'),
  autoAssignOrders: (orderNumbers) => api.post('/training/auto-assign', { orderNumbers }),
  assignOrdersToPM: (orderNumbers, pmEmail) => api.post('/training/assign-to-pm', { orderNumbers, pmEmail }),
  getWorkloadByMonth: () => api.get('/training/workload-by-month'),

  // Settings & Logs
  getSettings: (category) => api.get('/training/settings', { params: { category } }),
  getActivityLog: (pmId, limit) => api.get('/training/activity-log', { params: { pmId, limit } }),

  // PM Workload Configuration
  getPMWorkloadConfig: () => api.get('/training/pm-workload-config'),
  updatePMWorkloadConfig: (pmId, config) => api.put(`/training/pm-workload-config/${pmId}`, config),

  // Workload Settings
  getWorkloadSettings: () => api.get('/training/workload-settings'),
  updateWorkloadSettings: (settings) => api.put('/training/workload-settings', settings)
};

export default api;
